import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Car,
  Star,
  Clock,
  MapPin,
  Users,
  Bus,
  Phone,
  CheckCircle,
  ArrowRight,
  Shield,
  Zap,
  CreditCard,
  Award,
  Camera,
  Compass,
} from "lucide-react";
import { Button } from "../components/ui/button";
import BookingWidget from "../components/booking/BookingWidget";
import Header from "../components/layout/Header";
import heroImage from "../assets/cityTours.webp";
import city from "../assets/airportHome.webp";
import ctSedan1 from "../assets/ctSedan1.webp";
import ctVan2 from "../assets/ctVan2.webp";

const CityTours = () => {
  return (
    <div className="min-h-screen" data-testid="city-tours-page">
      <Header />

      {/* Hero Section - Matching Home Page Structure */}
      <section className="relative min-h-[90vh] lg:min-h-screen flex items-center pt-24 pb-16 lg:pt-32 lg:pb-20">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Paris City View"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-midnight-navy/80 via-midnight-navy/50 to-midnight-navy/30"></div>
        </div>

        <div className="container mx-auto px-4 lg:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-champagne-gold/20 text-champagne-gold rounded-full text-sm font-medium mb-4 lg:mb-6">
                <Camera className="w-4 h-4" />
                Private Chauffeur · Your Pace, Your Way
              </span>

              <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white leading-tight mb-4 lg:mb-6">
                Paris <span className="text-champagne-gold">City Tours</span> &
                Day Trips
              </h1>

              <p className="text-base md:text-lg text-paris-stone/90 mb-6 leading-relaxed max-w-xl">
                Explore Paris and beyond with your private chauffeur. From city
                highlights to Versailles, Mont Saint-Michel & Normandy.
                Customize your itinerary, travel at your own pace.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 md:gap-4 mb-6">
                <div className="flex items-center gap-2 text-white text-sm">
                  <Clock className="w-4 h-4 text-champagne-gold" />
                  <span>Flexible Hours</span>
                </div>
                <div className="flex items-center gap-2 text-white text-sm">
                  <Compass className="w-4 h-4 text-champagne-gold" />
                  <span>Custom Routes</span>
                </div>
                <div className="flex items-center gap-2 text-white text-sm">
                  <Users className="w-4 h-4 text-champagne-gold" />
                  <span>Up to 8 Guests</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 lg:gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-6 py-5 lg:px-8 lg:py-6 rounded-full shadow-lg hover:shadow-xl transition-all text-base lg:text-lg"
                  data-testid="tours-hero-book-btn"
                >
                  <Link to="/booking?tripType=hourly">
                    Book City Tour
                    <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white hover:text-midnight-navy font-semibold px-6 py-5 lg:px-8 lg:py-6 rounded-full transition-all text-base lg:text-lg"
                >
                  <Link to="/pricing">View Hourly Rates</Link>
                </Button>
              </div>

              {/* Urgency Microcopy */}
              <div className="mt-6 flex items-center gap-4 text-sm">
                <span className="flex items-center gap-2 text-green-400">
                  <Zap className="w-4 h-4" />
                  Instant confirmation
                </span>
                <span className="flex items-center gap-2 text-paris-stone/70">
                  <Clock className="w-4 h-4" />
                  Min. 2 hours booking
                </span>
              </div>

              {/* Phone CTA */}
              <div className="mt-6 lg:mt-8 flex items-center gap-3">
                <a
                  href="tel:+33763519524"
                  className="flex items-center gap-2 text-paris-stone hover:text-champagne-gold transition-colors"
                >
                  <div className="p-2 bg-champagne-gold/20 rounded-full">
                    <Phone className="w-4 h-4 lg:w-5 lg:h-5 text-champagne-gold" />
                  </div>
                  <div>
                    <p className="text-xs lg:text-sm text-paris-stone/70">
                      Call us 24/7
                    </p>
                    <p className="font-semibold text-white text-sm lg:text-base">
                      +33 763 519 524
                    </p>
                  </div>
                </a>
              </div>
            </motion.div>

            {/* Booking Widget - Desktop */}
            <div className="hidden lg:block py-8">
              <BookingWidget
                variant="hero"
                prefilledData={{ tripType: "hourly" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Booking Widget */}
      <section className="lg:hidden px-4 py-8 bg-paris-stone relative z-20">
        <BookingWidget variant="hero" prefilledData={{ tripType: "hourly" }} />
      </section>

      {/* Social Proof Section */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-midnight-navy">
                4.9/5
              </p>
              <div className="flex gap-1 justify-center my-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-champagne-gold text-champagne-gold"
                  />
                ))}
              </div>
              <p className="text-sm text-slate-600">Tour Reviews</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-midnight-navy">
                1500+
              </p>
              <p className="text-sm text-slate-600">Happy Customers</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-midnight-navy">
                €60
              </p>
              <p className="text-sm text-slate-600">Per Hour (Car)</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-midnight-navy">
                30km
              </p>
              <p className="text-sm text-slate-600">Included/Hour</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-20 bg-paris-stone">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600">
              Your private Paris tour in 3 simple steps
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: "1",
                title: "Choose Your Hours",
                desc: "Select duration (2-12 hours) and pickup location. Add destinations or let us suggest.",
                icon: Clock,
              },
              {
                step: "2",
                title: "Meet Your Chauffeur",
                desc: "Your professional driver picks you up and customizes the route to your preferences.",
                icon: MapPin,
              },
              {
                step: "3",
                title: "Explore & Enjoy",
                desc: "Visit landmarks, take photos, stop for lunch. Your tour, your pace.",
                icon: Camera,
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-champagne-gold rounded-full flex items-center justify-center mx-auto mb-4 text-midnight-navy font-bold text-2xl">
                  {item.step}
                </div>
                <h3 className="font-heading font-bold text-xl text-midnight-navy mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hourly Rates */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Simple Hourly Rates
            </h2>
            <p className="text-lg text-slate-600">
              No hidden fees · 30km included per hour · Pay driver on arrival
            </p>
          </motion.div>

          {/* Cards Grid */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                name: "Sedan Car",
                desc: "1-3 passengers",
                price: 60,
                features: [
                  "Mercedes E-Class or similar",
                  "30km included per hour",
                  "Professional English-speaking driver",
                  "Free cancellation 24h before",
                ],
                image: ctSedan1,
                buttonText: "Book Sedan Tour",
                buttonColor: "bg-champagne-gold",
                buttonTextColor: "text-midnight-navy",
              },
              {
                name: "Spacious Van",
                desc: "4-8 passengers",
                price: 70,
                features: [
                  "Mercedes V-Class or similar",
                  "30km included per hour",
                  "Perfect for families & groups",
                  "Free cancellation 24h before",
                ],
                image: ctVan2,
                buttonText: "Book Van Tour",
                buttonColor: "bg-midnight-navy",
                buttonTextColor: "text-white",
              },
            ].map((vehicle, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`rounded-2xl overflow-hidden shadow-soft hover:shadow-strong transition-all duration-300 border-2 ${
                  vehicle.buttonColor === "bg-champagne-gold"
                    ? "border-champagne-gold bg-paris-stone"
                    : "border-slate-200 bg-paris-stone"
                }`}
              >
                {/* Vehicle Image */}
                <div className="relative w-full h-48 md:h-56 lg:h-64 overflow-hidden">
                  <img
                    src={vehicle.image}
                    alt={vehicle.name}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                </div>

                {/* Card Content */}
                <div className="p-6">
                  {/* Icon */}
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mb-4 shadow">
                    {vehicle.name === "Sedan Car" ? (
                      <Car className="w-8 h-8 text-eiffel-blue" />
                    ) : (
                      <Bus className="w-8 h-8 text-midnight-navy" />
                    )}
                  </div>

                  <h3 className="font-heading font-bold text-2xl text-midnight-navy mb-1">
                    {vehicle.name}
                  </h3>
                  <p className="text-slate-600 mb-4">{vehicle.desc}</p>

                  {/* Price */}
                  <div className="text-center mb-6">
                    <span className="font-heading font-bold text-5xl text-champagne-gold">
                      €{vehicle.price}
                    </span>
                    <span className="text-slate-600 text-lg">/hour</span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 text-sm text-slate-600 mb-6">
                    {vehicle.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Book Button */}
                  <Button
                    asChild
                    className={`w-full ${vehicle.buttonColor} hover:${vehicle.buttonColor}/90 ${vehicle.buttonTextColor} font-bold rounded-full py-4`}
                  >
                    <Link to="/booking?tripType=hourly">
                      {vehicle.buttonText}
                    </Link>
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Footer Note */}
          <p className="text-center text-sm text-slate-500 mt-8">
            Minimum booking: 2 hours · Maximum: 12 hours · Pay driver upon
            completion
          </p>
        </div>
      </section>

      {/* Popular Tour Ideas */}
      <section className="py-16 md:py-20 bg-paris-stone">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Popular Tour Ideas
            </h2>
            <p className="text-lg text-slate-600">
              Suggested itineraries or create your own
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              {
                name: "Paris Highlights",
                duration: "4 hours",
                price: 240,
                desc: "Eiffel Tower, Louvre, Champs-Élysées, Montmartre",
                popular: true,
              },
              {
                name: "Versailles Day Trip",
                duration: "5 hours",
                price: 300,
                desc: "Palace of Versailles, gardens, Marie Antoinette's estate",
              },
              {
                name: "Mont Saint-Michel",
                duration: "10 hours",
                price: 600,
                desc: "Full day trip to the iconic island monastery",
              },
              {
                name: "Normandy & D-Day",
                duration: "10 hours",
                price: 600,
                desc: "D-Day beaches, American Cemetery, Normandy coast",
              },
            ].map((tour, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`rounded-xl p-6 border-2 ${
                  tour.popular
                    ? "bg-champagne-gold/10 border-champagne-gold"
                    : "bg-white border-slate-200"
                }`}
              >
                {tour.popular && (
                  <span className="inline-block px-2 py-1 bg-champagne-gold text-midnight-navy text-xs font-bold rounded mb-2">
                    MOST POPULAR
                  </span>
                )}
                <h3 className="font-heading font-bold text-lg text-midnight-navy mb-1">
                  {tour.name}
                </h3>
                <p className="text-sm text-slate-500 flex items-center gap-1 mb-2">
                  <Clock className="w-3 h-3" /> {tour.duration}
                </p>
                <p className="text-xs text-slate-600 mb-4">{tour.desc}</p>
                <div className="flex items-center justify-between">
                  <span className="font-heading font-bold text-xl text-champagne-gold">
                    €{tour.price}
                  </span>
                  <span className="text-xs text-slate-500">Sedan</span>
                </div>
                <Button
                  asChild
                  className="w-full mt-4 bg-midnight-navy hover:bg-midnight-navy/90 text-white rounded-full text-sm"
                >
                  <Link to="/booking?tripType=hourly">Book This Tour</Link>
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Why Choose Our City Tours?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: Compass,
                title: "Fully Customizable",
                desc: "Design your own route or let our drivers suggest the best spots",
              },
              {
                icon: Clock,
                title: "Flexible Timing",
                desc: "Stop whenever you want, stay as long as you like at each location",
              },
              {
                icon: CreditCard,
                title: "Transparent Pricing",
                desc: "Simple hourly rate with 30km included. No hidden surprises",
              },
              {
                icon: Award,
                title: "Expert Drivers",
                desc: "English-speaking professionals who know Paris inside out",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-14 h-14 bg-champagne-gold/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-champagne-gold" />
                </div>
                <h3 className="font-heading font-bold text-lg text-midnight-navy mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-20 bg-paris-stone">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              What Tour Guests Say
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                name: "Anna S.",
                text: "Perfect way to see Paris! Our driver knew all the best photo spots and even recommended a lovely café for lunch. Much better than a bus tour.",
                rating: 5,
              },
              {
                name: "Robert M.",
                text: "Booked the Versailles tour. Driver was punctual, car was immaculate. Having flexibility to explore at our own pace was fantastic.",
                rating: 5,
              },
              {
                name: "Lisa K.",
                text: "Did a custom tour with Mont Saint-Michel. Long day but so worth it. Driver was knowledgeable and made the journey comfortable.",
                rating: 5,
              },
            ].map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-soft"
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-champagne-gold text-champagne-gold"
                    />
                  ))}
                </div>
                <p className="text-slate-600 mb-4 italic">"{review.text}"</p>
                <p className="font-semibold text-midnight-navy">
                  {review.name}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: "What's included in the hourly rate?",
                a: "Your hourly rate includes the vehicle, professional driver, 30km per hour of travel, and all waiting time. Tolls and parking for attractions are additional.",
              },
              {
                q: "Can I customize my tour itinerary?",
                a: "Absolutely! You can plan your own route or discuss options with your driver. They're happy to suggest popular stops and hidden gems.",
              },
              {
                q: "What's the minimum booking duration?",
                a: "The minimum booking is 2 hours. For day trips like Mont Saint-Michel or Normandy, we recommend 8-10 hours.",
              },
              {
                q: "Do drivers speak English?",
                a: "Yes, all our drivers speak English and are knowledgeable about Paris and its surroundings. They can provide commentary on landmarks.",
              },
              {
                q: "Can we stop for lunch during the tour?",
                a: "Of course! You're free to stop wherever you like. Your driver can recommend restaurants or wait while you dine.",
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-paris-stone rounded-xl p-6"
              >
                <h3 className="font-semibold text-midnight-navy mb-2">
                  {faq.q}
                </h3>
                <p className="text-slate-600">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-midnight-navy">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Explore Paris Your Way
            </h2>
            <p className="text-paris-stone/80 mb-6 max-w-xl mx-auto">
              Book your private city tour now. Flexible hours, custom routes,
              professional drivers.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-8 py-6 rounded-full shadow-lg"
                data-testid="tours-cta-book-btn"
              >
                <Link to="/booking?tripType=hourly">
                  Book Now - From €60/hr
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white hover:text-midnight-navy font-semibold px-8 py-6 rounded-full"
              >
                <a href="tel:+33763519524">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Us
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sticky CTA for Mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
        <Button
          asChild
          className="w-full bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold py-4 rounded-full"
        >
          <Link to="/booking?tripType=hourly">
            Book Your City Tour
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </Button>
      </div>

      <div className="lg:hidden h-20"></div>
    </div>
  );
};

export default CityTours;
